import { Component, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { BuildingContactView, BuildingView, ContactView, SearchContactsForBuildingParameters } from '@api-proxies';
import { BuildingContactService } from '@http/clients/building-contact.service';
import { ContactService } from '@http/clients/contact.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { AwaiterService } from '@services/awaiter.service';
import { GridService } from '@services/grid.service';
import { IKebabAction } from '@shared/grids/custom-cell-actions/custom-cell-actions.component';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ModalEditBuildingContactComponent } from '@shared/modals/modal-edit-building-contact/modal-edit-building-contact.component';
import { TypeAheadContactsComponent } from '@shared/type-ahead-contacts/type-ahead-contacts.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { BUILDING_CONTACTS_GRID_COLUMNS } from './buildings-single-contacts.config';

@Component({
    selector: 'app-buildings-single-contacts',
    templateUrl: './buildings-single-contacts.component.html',
    styleUrls: ['./buildings-single-contacts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, TypeAheadContactsComponent]
})
export class BuildingsSingleContactsComponent {
    building = input<BuildingView>();

    buildingChange = output<void>();
    tabCounterChange = output<void>();

    contactsDatasource?: IServerSideDatasource;
    gridColumns: ColDef[] = [...BUILDING_CONTACTS_GRID_COLUMNS];
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    typeAheadSearchByEntityParameters: ITypeAheadDropdownEntityParameters = {
        entityKeyName: EntityTypes.Building
    };
    private readonly contactService = inject(ContactService);
    private readonly buildingContactService = inject(BuildingContactService);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly gridService = inject(GridService);

    constructor() {
        this.setupGridColumns();

        effect(() => {
            const building = this.building();
            const buildingId = building?.id;

            if (!buildingId) return;

            this.typeAheadSearchByEntityParameters = {
                entityKeyName: EntityTypes.Building,
                entity: {
                    buildingId
                }
            };

            if (!this.contactsDatasource) {
                this.createDatasource();
                return;
            }

            this.refreshGridView();
        });
    }

    getRowIdFn = (params: any) => params.data?.contactId?.toString();

    async onSelectedContact(contact: ContactView): Promise<void> {
        const buildingId = this.building()?.id;
        if (!contact || !buildingId) return;

        try {
            const contactSaved = await this.buildingContactService.save({
                contactId: contact.id,
                buildingId: buildingId
            } as BuildingContactView);

            if (contactSaved) {
                this.tabCounterChange.emit();
                const gridApi = this.gridService.getGridApi();
                let applied = false;

                if (gridApi?.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        add: [contactSaved]
                    });
                    applied = transactionResult?.status === 'Applied';
                }

                if (!applied) {
                    this.refreshGridView();
                }

                this.noResults.set(false);
            }
        } catch (error) {
            console.error(error);
            this.snackbarNotificationService.error('An error occurred while trying to save the contact');
        }
    }

    async editItem(buildingContact: BuildingContactView): Promise<void> {
        const buildingContactUpdated = await this.modalService.open(ModalEditBuildingContactComponent, {
            contact: buildingContact
        });

        if (buildingContactUpdated) {
            const gridApi = this.gridService.getGridApi();

            if (gridApi) {
                const node = gridApi.getRowNode(buildingContactUpdated.contactId.toString());
                if (node?.data) {
                    Object.assign(node.data, buildingContactUpdated);
                    gridApi.redrawRows({ rowNodes: [node] });
                }
            }
        }
    }

    async removeItem(buildingContact: BuildingContactView): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove contact',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this contact from the contact list?',
            title: 'Remove contact'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction(
            'Removing contact',
            async () => {
                if (!buildingContact) return;

                await this.buildingContactService.remove({
                    id: buildingContact.id,
                    contactId: buildingContact.contactId,
                    buildingId: buildingContact.buildingId
                } as BuildingContactView);

                this.tabCounterChange.emit();
                const gridApi = this.gridService.getGridApi();
                let removed = false;

                if (gridApi?.applyServerSideTransaction) {
                    const node = gridApi.getRowNode(buildingContact.contactId.toString());
                    if (node?.data) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            remove: [node.data]
                        });
                        removed = transactionResult?.status === 'Applied';
                    }
                }

                if (removed && gridApi) {
                    this.noResults.set(gridApi.getDisplayedRowCount() === 0);
                } else {
                    this.refreshGridView();
                }

                if (buildingContact.buildingContactRoleId === 1) {
                    this.buildingChange.emit();
                }
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async updateContactFreezedData(buildingContact: BuildingContactView): Promise<void> {
        const modalContent = `Company&emsp;&emsp;${buildingContact?.companyName ?? '[No data]'} &ensp;>&ensp; ${
            buildingContact?.currentCompanyName ?? '[No data]'
        }`;

        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Update Data',
            cancelCaption: 'Cancel',
            bodyTitle: 'The following data will be updated:',
            content: modalContent,
            title: 'Data update available'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction(
            'Updating contact data',
            async () => {
                const buildingContactUpdated = await this.buildingContactService.buildingContactUpdateCompany(
                    buildingContact.id
                );

                if (buildingContactUpdated) {
                    this.snackbarNotificationService.success(
                        `${buildingContactUpdated.contactFullName}'s company has been successfully updated.`
                    );
                    const gridApi = this.gridService.getGridApi();
                    if (gridApi) {
                        const node = gridApi.getRowNode(buildingContactUpdated.contactId.toString());
                        if (node?.data) {
                            Object.assign(node.data, buildingContactUpdated);
                            gridApi.redrawRows({ rowNodes: [node] });
                        }
                    }
                }
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Edit',
                action: (contact: BuildingContactView) => this.editItem(contact)
            },
            {
                label: 'Remove',
                action: (contact: BuildingContactView) => this.removeItem(contact)
            },
            {
                label: 'Update Data',
                action: (contact: BuildingContactView) => this.updateContactFreezedData(contact),
                show: (contact: BuildingContactView) => contact.companyId !== contact.currentCompanyId
            }
        ];

        this.gridColumns = BUILDING_CONTACTS_GRID_COLUMNS.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchContactsForBuildingParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;

        const buildingId = this.building()?.id;
        if (!buildingId) {
            throw new Error('Building is required to search contacts for building.');
        }

        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            buildingId: buildingId
        } as SearchContactsForBuildingParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.contactsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.contactService.searchContactsForBuilding(searchParams);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching contacts:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }
}
